import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import tzLookup from "tz-lookup";
import type { City, CityWithTimes, DaySegment, StopActions, TripLeg } from "../data/types";
import { DAY_COLORS } from "../data/design-tokens";

type Place = Pick<City, "latitude" | "longitude">;

/** Parse the route duration format without searching repeatedly through invalid input. */
export function durationMinutes(duration: string): number {
  const match = /^(?:(\d+)h\s*)?(?:(\d+)m)?$/.exec(duration.trim());
  if (!match) return 0;
  const minutes = Number(match[1] ?? 0) * 60 + Number(match[2] ?? 0);
  return Number.isFinite(minutes) ? minutes : 0;
}

export function stationTimezone(place: Place): string {
  return tzLookup(place.latitude, place.longitude);
}

/** Calendar arithmetic deliberately has no browser timezone or DST dependency. */
export function addCalendarDays(date: string, days: number): string {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function calendarDaysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

/** Offset-bearing strings already denote instants; search inputs are station-local. */
export function journeyInstant(value: string, place: Place): string {
  const date = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value)
    ? new Date(value) : fromZonedTime(value, stationTimezone(place));
  if (!Number.isFinite(date.getTime())) throw new Error("Invalid journey date");
  return date.toISOString();
}

export function stationDateTime(instant: string | null | undefined, place: Place) {
  if (!instant || !Number.isFinite(Date.parse(instant))) return { date: undefined, time: undefined };
  const value = formatInTimeZone(instant, stationTimezone(place), "yyyy-MM-dd'T'HH:mm");
  return { date: value.slice(0, 10), time: value.slice(11) };
}

export function legArrivalInstant(leg: TripLeg | undefined, at: Place): string | undefined {
  if (leg?.arrivalInstant) return leg.arrivalInstant;
  if (leg?.arrivalDate && leg.arrivalTime) return journeyInstant(`${leg.arrivalDate}T${leg.arrivalTime}:00`, at);
}

/** Arrival plus a stop, measured at the departure station, including multi-night stays. */
export function nextDeparture(from: City, previous: TripLeg | undefined, actions: StopActions, date: string): string {
  const arrival = legArrivalInstant(previous, from);
  const localDate = stationDateTime(arrival, from).date ?? date;
  const action = actions[from.id];
  if (action?.overnight) {
    return journeyInstant(`${addCalendarDays(localDate, action.nights || 1)}T09:00:00`, from);
  }
  if (arrival) return new Date(Date.parse(arrival) + 2 * 3_600_000).toISOString();
  return journeyInstant(`${localDate}T09:00:00`, from);
}

type JourneyTimes = Pick<TripLeg, "stations" | "stationData" | "routeCoords" | "departureDate" | "arrivalDate" | "departureInstant" | "arrivalInstant">;
export interface JourneyDaySegment extends DaySegment { coordinates?: [number, number][] }

/** A station's arrival and departure can belong to different itinerary days. */
export function journeyStationDay(journey: JourneyTimes, station: CityWithTimes, startDay: number, departure = false): number {
  const instant = departure ? station.departureInstant || station.arrivalInstant : station.arrivalInstant || station.departureInstant;
  const date = stationDateTime(instant, station).date ?? (!departure && station.id === journey.stations.at(-1) ? journey.arrivalDate : journey.departureDate);
  if (!journey.departureDate || !date) return startDay;
  return startDay + Math.max(0, calendarDaysBetween(journey.departureDate, date));
}

function journeyVisits(journey: JourneyTimes): CityWithTimes[] {
  const visits = (journey.stationData ?? []).filter(station => Number.isFinite(station.latitude) && Number.isFinite(station.longitude));
  return visits.map((station, index) => ({
    ...station,
    departureInstant: station.departureInstant || (index === 0 ? journey.departureInstant : undefined),
    arrivalInstant: station.arrivalInstant || (index === visits.length - 1 ? journey.arrivalInstant : undefined),
  }));
}

function midnightFraction(from: CityWithTimes, to: CityWithTimes, date: string, fallback: number): number {
  const departure = Date.parse(from.departureInstant || from.arrivalInstant || "");
  const arrival = Date.parse(to.arrivalInstant || to.departureInstant || "");
  if (!Number.isFinite(departure) || !Number.isFinite(arrival) || arrival <= departure) return fallback;
  const midnight = Date.parse(journeyInstant(`${date}T00:00:00`, from));
  return Math.max(0, Math.min(1, (midnight - departure) / (arrival - departure)));
}

function routeEdgeCoordinates(coordinates: [number, number][] | undefined, from: City, to: City): [number, number][] {
  const matches = (point: [number, number], station: City) => Math.abs(point[0] - station.latitude) < 0.000001 && Math.abs(point[1] - station.longitude) < 0.000001;
  const start = coordinates?.findIndex(point => matches(point, from)) ?? -1;
  const end = coordinates?.findIndex((point, index) => index > start && matches(point, to)) ?? -1;
  if (coordinates && start >= 0 && end > start) return coordinates.slice(start, end + 1);
  return [[from.latitude, from.longitude], [to.latitude, to.longitude]];
}

function splitCoordinatePath(coordinates: [number, number][], fractions: number[]): [number, number][][] {
  const progress = coordinates.map((_point, index) => index / (coordinates.length - 1));
  const atFraction = (fraction: number): [number, number] => {
    const end = Math.max(1, progress.findIndex(value => value >= fraction));
    const local = (fraction - progress[end - 1]) / (progress[end] - progress[end - 1] || 1);
    const from = coordinates[end - 1], to = coordinates[end];
    return [from[0] + (to[0] - from[0]) * local, from[1] + (to[1] - from[1]) * local];
  };
  const boundaries = [0, ...fractions, 1];
  return boundaries.slice(1).map((end, index) => {
    const start = boundaries[index];
    const inside = coordinates.filter((_point, i) => progress[i] > start && progress[i] < end);
    return [atFraction(start), ...inside, atFraction(end)];
  });
}

function journeyEdgeSegments(journey: JourneyTimes, from: CityWithTimes, to: CityWithTimes, fromDay: number, toDay: number, startDay: number) {
  const fractions: number[] = [];
  for (let day = fromDay + 1; day <= toDay; day++) {
    const fallback = (day - fromDay) / (toDay - fromDay + 1);
    fractions.push(journey.departureDate ? midnightFraction(from, to, addCalendarDays(journey.departureDate, day - startDay), fallback) : fallback);
  }
  return splitCoordinatePath(routeEdgeCoordinates(journey.routeCoords, from, to), fractions);
}

/** Split the displayed station-to-station path at midnight, including two-stop sleepers. */
export function journeyDaySegments(journey: JourneyTimes, startDay = 1, startColor = DAY_COLORS[(startDay - 1) % DAY_COLORS.length]): JourneyDaySegment[] {
  const visits = journeyVisits(journey);
  if (visits.length < 2) return journey.stations.length
    ? [{ day: startDay, color: startColor, stationIds: journey.stations, coordinates: journey.routeCoords }] : [];
  const segments: JourneyDaySegment[] = [];
  const append = (day: number, point: [number, number], stationId?: string) => {
    let segment = segments.at(-1);
    if (segment?.day !== day) {
      segment = { day, color: day === startDay ? startColor : DAY_COLORS[(day - 1) % DAY_COLORS.length], stationIds: [], coordinates: [] };
      segments.push(segment);
    }
    const lastPoint = segment.coordinates!.at(-1);
    if (lastPoint?.[0] !== point[0] || lastPoint?.[1] !== point[1]) segment.coordinates!.push(point);
    if (stationId && segment.stationIds.at(-1) !== stationId) segment.stationIds.push(stationId);
  };
  append(startDay, [visits[0].latitude, visits[0].longitude], visits[0].id);
  for (let i = 1; i < visits.length; i++) {
    const from = visits[i - 1], to = visits[i];
    const fromDay = Math.max(segments.at(-1)!.day, journeyStationDay(journey, from, startDay, true));
    let toDay = journeyStationDay(journey, to, startDay);
    if (i === visits.length - 1 && journey.departureDate && journey.arrivalDate) {
      toDay = Math.max(toDay, startDay + calendarDaysBetween(journey.departureDate, journey.arrivalDate));
    }
    append(fromDay, [from.latitude, from.longitude], from.id);
    journeyEdgeSegments(journey, from, to, fromDay, toDay, startDay).forEach((points, offset) => {
      for (const point of points) append(fromDay + offset, point);
    });
    append(Math.max(fromDay, toDay), [to.latitude, to.longitude], to.id);
  }
  if (segments.length === 1 && journey.routeCoords?.length) segments[0].coordinates = journey.routeCoords;
  return segments;
}
